import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  Bell,
  ArrowRightIcon,
  Video,
  Vote,
  UsersRound,
  AlarmClock,
  MessageSquare,
  X
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { useChatNotifications } from "@/components/chat";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/integrated-auth";
import type { UserDetails } from "@/types/user";
import {
  dismissNotifications,
  getDismissedNotificationIds,
} from "@/lib/notification-dismissals";

interface YourTurnResponse {
  userId: string;
  needsAction: boolean;
  actionType?: 'submission' | 'voting';
  leagueId?: number;
  leagueName?: string;
  roundId?: number;
  deadline?: string;
  themeColor?: string;
  pendingCount?: number; // number of people who still need to submit/vote
}

interface Notification {
  id: string;
  type: 'submission' | 'voting' | 'results' | 'invitation' | 'deadline' | 'chat';
  message: string;
  league: {
    id: number;
    name: string;
    themeColor?: string;
  };
  read: boolean;
  timestamp: Date;
  pendingCount?: number; // number of people who still need to submit/vote
  chatTab?: boolean; // whether to open chat tab when clicked
}

export function NotificationMenu({ mobile = false }: { mobile?: boolean }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState<string[]>([]);
  const { toast } = useToast();
  
  // Use auth context instead of a separate /api/user query (avoids duplicate fetch)
  const { user: authUser, isAuthenticated } = useAuth();
  const userId = authUser?.id;
  const dismissedNotificationIdSet = useMemo(
    () => new Set(dismissedNotificationIds),
    [dismissedNotificationIds]
  );
  
  // Get user's turn status and other notifications
  const { data: yourTurnData } = useQuery<YourTurnResponse>({
    queryKey: ['/api/user/your-turn'],
    enabled: isAuthenticated,
  });
  
  // Get user deadlines
  const { data: deadlinesData } = useQuery<Array<{
    leagueId: number;
    leagueName: string;
    roundId: number;
    themeColor?: string;
    deadline: string;
    phase: string;
  }>>({
    queryKey: ['/api/user/deadlines'],
    enabled: isAuthenticated,
  });
  
  // Get user's active leagues (single query for both member data and chat notifications)
  const { data: userLeaguesData } = useQuery<any[]>({
    queryKey: ['/api/leagues/user/active'],
    enabled: isAuthenticated,
    staleTime: 60000,
  });
  const leagueMembersData = userLeaguesData;

  // Prepare leagues array for chat notifications hook
  const leaguesForChat = useMemo(() => {
    if (!userLeaguesData || !Array.isArray(userLeaguesData)) return [];
    return userLeaguesData.map(league => ({
      id: league.id,
      name: league.name,
      themeColor: league.themeColor,
    }));
  }, [userLeaguesData]);

  // Get chat notifications across all leagues
  const {
    chatNotifications,
    markLeagueAsRead,
  } = useChatNotifications({
    leagues: leaguesForChat,
    enabled: isAuthenticated && leaguesForChat.length > 0,
  });

  useEffect(() => {
    if (!userId) {
      setDismissedNotificationIds([]);
      return;
    }

    setDismissedNotificationIds(getDismissedNotificationIds(userId));
  }, [userId]);

  // Convert your-turn data and deadlines into notifications
  useEffect(() => {
    const newNotifications: Notification[] = [];
    
    // Add Your Turn notification if applicable
    if (yourTurnData?.needsAction && yourTurnData.leagueId) {
      const actionType = yourTurnData.actionType || 'submission';
      const actionMessage = actionType === 'submission' 
        ? "It's your turn to submit a video" 
        : "It's your turn to vote on submissions";
      
      const matchingDeadline = deadlinesData?.find(d => d.roundId === yourTurnData.roundId)
        || deadlinesData?.find(d => d.leagueId === yourTurnData.leagueId);
      const leagueName = yourTurnData.leagueName || matchingDeadline?.leagueName || 'League';
      const themeColor = yourTurnData.themeColor || matchingDeadline?.themeColor;
      
      // Get member data for the league to determine actual waiting counts
      const leagueMembers = leagueMembersData?.[yourTurnData.leagueId || 0] || [];
      
      // Calculate accurate waiting counts based on league members
      let pendingCount = 0;
      
      if (yourTurnData.pendingCount !== undefined) {
        // Use API-provided count if available
        pendingCount = yourTurnData.pendingCount;
      } else if (actionType === 'submission') {
        // For submission: total members minus current user (who needs to submit)
        pendingCount = Math.max(0, leagueMembers.length - 1);
      } else {
        // For voting: default to 1 if we can't determine 
        pendingCount = 1;
      }
      
      newNotifications.push({
        id: `your-turn-${yourTurnData.leagueId}-${yourTurnData.roundId ?? 'current'}-${actionType}`,
        type: actionType as 'submission' | 'voting',
        message: `${actionMessage} in ${leagueName}`,
        league: {
          id: yourTurnData.leagueId,
          name: leagueName,
          themeColor
        },
        read: false,
        timestamp: new Date(),
        pendingCount
      });
    }
    
    // Add deadline notifications
    if (deadlinesData && deadlinesData.length > 0) {
      deadlinesData.forEach(deadline => {
        const isCloseDeadline = new Date(deadline.deadline).getTime() - new Date().getTime() < 24 * 60 * 60 * 1000; // within 24 hours

        if (isCloseDeadline) {
          const actionType = deadline.phase === 'submission' ? 'submission' : 'voting';
          const actionVerb = deadline.phase === 'submission' ? 'submit' : 'vote';

          newNotifications.push({
            id: `deadline-${deadline.roundId}-${actionType}`,
            type: actionType,
            message: `Deadline to ${actionVerb} in ${deadline.leagueName} is approaching`,
            league: {
              id: deadline.leagueId,
              name: deadline.leagueName,
              themeColor: deadline.themeColor
            },
            read: false,
            timestamp: new Date(deadline.deadline)
          });
        }
      });
    }

    // Add chat notifications
    if (chatNotifications && chatNotifications.length > 0) {
      chatNotifications.forEach(chatNotif => {
        const messagePreview = chatNotif.lastMessageAuthor && chatNotif.lastMessagePreview
          ? `${chatNotif.lastMessageAuthor}: "${chatNotif.lastMessagePreview}${chatNotif.lastMessagePreview.length >= 50 ? '...' : ''}"`
          : `${chatNotif.unreadCount} new message${chatNotif.unreadCount !== 1 ? 's' : ''}`;

        newNotifications.push({
          id: `chat-${chatNotif.leagueId}`,
          type: 'chat',
          message: `${messagePreview} in ${chatNotif.leagueName}`,
          league: {
            id: chatNotif.leagueId,
            name: chatNotif.leagueName,
            themeColor: chatNotif.themeColor
          },
          read: false,
          timestamp: chatNotif.lastMessageAt || new Date(),
          pendingCount: chatNotif.unreadCount,
          chatTab: true,
        });
      });
    }

    setNotifications(
      newNotifications.filter(notification =>
        notification.type === 'chat' || !dismissedNotificationIdSet.has(notification.id)
      )
    );
  }, [chatNotifications, deadlinesData, dismissedNotificationIdSet, leagueMembersData, yourTurnData]);
  
  const hasUnreadNotifications = notifications.some(n => !n.read);

  const handleNotificationClick = (notification: Notification) => {
    setIsOpen(false);

    if (notification.type === 'chat') {
      markLeagueAsRead(notification.league.id);
      return;
    }

    if (!userId) {
      return;
    }

    setDismissedNotificationIds(dismissNotifications(userId, [notification.id]));
  };

  const handleClearAll = () => {
    if (notifications.length === 0) {
      return;
    }

    notifications
      .filter(notification => notification.type === 'chat')
      .forEach(notification => {
        markLeagueAsRead(notification.league.id);
      });

    if (userId) {
      const dismissibleIds = notifications
        .filter(notification => notification.type !== 'chat')
        .map(notification => notification.id);

      if (dismissibleIds.length > 0) {
        setDismissedNotificationIds(dismissNotifications(userId, dismissibleIds));
      }
    }

    setNotifications([]);
    toast({ title: 'Notifications cleared' });
  };
  
  if (!isAuthenticated) {
    return null;
  }
  
  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    // convert to seconds
    const seconds = Math.floor(diff / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    
    return `${Math.floor(seconds / 86400)}d ago`;
  };
  
  // For mobile, we use the same popover but with slightly different styles  
  if (mobile) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div 
            className="flex flex-col items-center p-2 text-muted-foreground cursor-pointer relative"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Bell className={isActive("/notifications") || isOpen ? "text-primary" : ""} size={22} />
            {hasUnreadNotifications && (
              <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-red-500 border border-background"></span>
            )}
            {/* Removed text label for cleaner mobile navigation */}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[90vw] max-w-[350px] p-0" align="end" side="bottom">
          <div className="bg-accent/50 p-3 border-b flex items-center justify-between gap-2">
            <h3 className="font-medium">Notifications</h3>
            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-sm font-medium text-primary hover:underline"
                  aria-label="Clear all notifications"
                >
                  Clear all
                </button>
              )}
              <button 
                onClick={() => setIsOpen(false)} 
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close notifications"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <p>No notifications</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {notifications.map(notification => {
                  const href = notification.chatTab
                    ? `/leagues/${notification.league.id}?tab=chat`
                    : `/leagues/${notification.league.id}`;

                  return (
                    <li key={notification.id} className="p-3 hover:bg-accent/30 transition-colors">
                      <Link href={href} onClick={() => handleNotificationClick(notification)}>
                        <div className="flex items-start gap-3 cursor-pointer">
                          {notification.type === 'submission' ? (
                            <div className="flex items-center mt-1 shrink-0">
                              <span className="text-sm mr-1">🎬</span>
                              <Video className="h-5 w-5 text-primary" />
                            </div>
                          ) : notification.type === 'voting' ? (
                            <div className="flex items-center mt-1 shrink-0">
                              <span className="text-sm mr-1">🗳️</span>
                              <Vote className="h-5 w-5 text-secondary" />
                            </div>
                          ) : notification.type === 'deadline' ? (
                            <div className="flex items-center mt-1 shrink-0">
                              <span className="text-sm mr-1">⏰</span>
                              <AlarmClock className="h-5 w-5 text-orange-500" />
                            </div>
                          ) : notification.type === 'chat' ? (
                            <div className="flex items-center mt-1 shrink-0">
                              <span className="text-sm mr-1">💬</span>
                              <MessageSquare className="h-5 w-5 text-blue-500" />
                            </div>
                          ) : (
                            <div
                              className="w-2 h-2 mt-2 rounded-full"
                              style={{ backgroundColor: notification.league.themeColor || 'var(--primary)' }}
                            ></div>
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium">{notification.message}</p>
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-xs text-muted-foreground">
                                {formatTimeAgo(notification.timestamp)}
                              </p>
                              {notification.type === 'chat' && notification.pendingCount && notification.pendingCount > 0 ? (
                                <Badge variant="secondary" className="text-xs">
                                  {notification.pendingCount} new
                                </Badge>
                              ) : notification.pendingCount && notification.pendingCount > 0 ? (
                                <div className="flex items-center text-xs text-muted-foreground">
                                  <UsersRound className="h-3 w-3 mr-1" />
                                  <span>{notification.pendingCount} waiting</span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <ArrowRightIcon className="h-4 w-4 mt-1 text-muted-foreground" />
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="bg-accent/30 p-2 border-t text-center">
            <Link href="/notifications" onClick={() => setIsOpen(false)}>
              <span className="text-sm text-primary font-medium hover:underline">
                View all notifications
              </span>
            </Link>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div 
          className="text-dark hover:text-primary transition-colors cursor-pointer relative"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Bell size={22} />
          {hasUnreadNotifications && (
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-red-500 border border-background"></span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="bg-accent/50 p-3 border-b flex items-center justify-between gap-2">
          <h3 className="font-medium">Notifications</h3>
          <div className="flex items-center gap-2">
            {notifications.length > 0 && (
              <button
                onClick={handleClearAll}
                className="text-sm font-medium text-primary hover:underline"
                aria-label="Clear all notifications"
              >
                Clear all
              </button>
            )}
            <button 
              onClick={() => setIsOpen(false)} 
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close notifications"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <p>No notifications</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {notifications.map(notification => {
                const href = notification.chatTab
                  ? `/leagues/${notification.league.id}?tab=chat`
                  : `/leagues/${notification.league.id}`;

                return (
                  <li key={notification.id} className="p-3 hover:bg-accent/30 transition-colors">
                    <Link href={href} onClick={() => handleNotificationClick(notification)}>
                      <div className="flex items-start gap-3 cursor-pointer">
                        {notification.type === 'submission' ? (
                          <div className="flex items-center mt-1 shrink-0">
                            <span className="text-sm mr-1">🎬</span>
                            <Video className="h-5 w-5 text-primary" />
                          </div>
                        ) : notification.type === 'voting' ? (
                          <div className="flex items-center mt-1 shrink-0">
                            <span className="text-sm mr-1">🗳️</span>
                            <Vote className="h-5 w-5 text-secondary" />
                          </div>
                        ) : notification.type === 'deadline' ? (
                          <div className="flex items-center mt-1 shrink-0">
                            <span className="text-sm mr-1">⏰</span>
                            <AlarmClock className="h-5 w-5 text-orange-500" />
                          </div>
                        ) : notification.type === 'chat' ? (
                          <div className="flex items-center mt-1 shrink-0">
                            <span className="text-sm mr-1">💬</span>
                            <MessageSquare className="h-5 w-5 text-blue-500" />
                          </div>
                        ) : (
                          <div
                            className="w-2 h-2 mt-2 rounded-full"
                            style={{ backgroundColor: notification.league.themeColor || 'var(--primary)' }}
                          ></div>
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium">{notification.message}</p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-xs text-muted-foreground">
                              {formatTimeAgo(notification.timestamp)}
                            </p>
                            {notification.type === 'chat' && notification.pendingCount && notification.pendingCount > 0 ? (
                              <Badge variant="secondary" className="text-xs">
                                {notification.pendingCount} new
                              </Badge>
                            ) : notification.pendingCount && notification.pendingCount > 0 ? (
                              <div className="flex items-center text-xs text-muted-foreground">
                                <UsersRound className="h-3 w-3 mr-1" />
                                <span>{notification.pendingCount} waiting</span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <ArrowRightIcon className="h-4 w-4 mt-1 text-muted-foreground" />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Helper function to determine if a path is active
function isActive(path: string) {
  const location = window.location.pathname;
  if (path === "/" && location === "/") return true;
  if (path !== "/" && location.startsWith(path)) return true;
  return false;
}
